import Seo from "@/components/Seo";
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ArrowLeftRight, X, Sparkles, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  all: "All Work",
  roofing: "Roofing",
  tiling: "Tiling",
  plumbing: "Plumbing",
  electrical: "Electrical",
  carpentry: "Carpentry",
  painting: "Painting",
  remodeling: "Remodeling",
  cleaning: "Cleaning",
  lawn_care: "Lawn Care",
  hvac: "HVAC",
  pest_control: "Pest Control",
  general_care: "General Care",
  metalwork: "Metalwork & Welding",
  other: "Other",
};

function ProjectCard({ project, onOpen }) {
  const [showAfter, setShowAfter] = useState(false);
  const apiOrigin = process.env.REACT_APP_BACKEND_URL;

  return (
    <div
      className="break-inside-avoid mb-4 rounded-xl overflow-hidden bg-white shadow-md hover:shadow-xl transition-shadow group cursor-pointer relative"
      data-testid={`portfolio-card-${project.id}`}
    >
      <div
        className="relative w-full"
        onClick={() => onOpen(project)}
        data-testid={`portfolio-card-image-${project.id}`}
      >
        <img
          src={`${apiOrigin}${showAfter ? project.after_url : project.before_url}`}
          alt={showAfter ? "After" : "Before"}
          className="w-full h-auto block transition-opacity duration-300"
          loading="lazy"
        />
        <div className="absolute top-2 left-2 bg-black/70 text-white text-xs font-semibold px-2 py-1 rounded">
          {showAfter ? "AFTER" : "BEFORE"}
        </div>
        <div className="absolute bottom-2 left-2 bg-orange-500 text-white text-xs font-semibold px-2 py-1 rounded">
          {CATEGORY_LABELS[project.service_category] || project.service_category}
        </div>
      </div>
      <div className="p-3 flex items-center justify-between border-t border-slate-100">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowAfter((v) => !v);
          }}
          className="text-xs font-semibold text-slate-700 hover:text-orange-500 flex items-center gap-1.5"
          data-testid={`portfolio-toggle-${project.id}`}
        >
          <ArrowLeftRight className="w-3.5 h-3.5" />
          Show {showAfter ? "Before" : "After"}
        </button>
        <button
          type="button"
          onClick={() => onOpen(project)}
          className="text-xs font-semibold text-orange-500 hover:text-orange-600"
          data-testid={`portfolio-expand-${project.id}`}
        >
          View Both
        </button>
      </div>
    </div>
  );
}

function ProjectLightbox({ project, onClose }) {
  const apiOrigin = process.env.REACT_APP_BACKEND_URL;
  if (!project) return null;
  return (
    <div
      className="fixed inset-0 bg-black/80 z-[200] flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="portfolio-lightbox"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 text-white p-2 rounded-full hover:bg-white/20"
        data-testid="portfolio-lightbox-close"
      >
        <X className="w-6 h-6" />
      </button>
      <div
        className="max-w-5xl w-full bg-white rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wide text-orange-500">
              {CATEGORY_LABELS[project.service_category] || project.service_category}
            </span>
          </div>
          <Link
            to="/booking"
            className="text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 px-4 py-2 rounded-md"
            data-testid="portfolio-lightbox-book"
          >
            Book Similar Job
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1 bg-slate-100">
          <div className="relative">
            <img
              src={`${apiOrigin}${project.before_url}`}
              alt="Before"
              className="w-full h-full object-cover"
            />
            <div className="absolute top-3 left-3 bg-black/70 text-white text-xs font-bold px-3 py-1.5 rounded">
              BEFORE
            </div>
          </div>
          <div className="relative">
            <img
              src={`${apiOrigin}${project.after_url}`}
              alt="After"
              className="w-full h-full object-cover"
            />
            <div className="absolute top-3 left-3 bg-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded">
              AFTER
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PortfolioPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/portfolio`)
      .then((res) => {
        if (!cancelled) setProjects(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setProjects([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const availableCategories = useMemo(() => {
    const set = new Set(projects.map((p) => p.service_category));
    return ["all", ...Array.from(set)];
  }, [projects]);

  const filtered = useMemo(() => {
    if (activeCategory === "all") return projects;
    return projects.filter((p) => p.service_category === activeCategory);
  }, [projects, activeCategory]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Seo
        title="Project Portfolio — Before & After Transformations"
        description="Browse real before-and-after handyman projects completed by ProToCall across Saint Lucia and the Caribbean. See the quality of our work before you book."
        path="/portfolio"
      />
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="flex items-center gap-2 text-orange-400 text-sm font-semibold mb-3">
            <Sparkles className="w-4 h-4" />
            OUR WORK
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
            Project Portfolio
          </h1>
          <p className="text-base md:text-lg text-slate-300 max-w-2xl">
            Real before-and-after transformations from jobs we've completed
            across Saint Lucia. See the quality before you book.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/booking">
              <Button
                className="bg-orange-500 hover:bg-orange-600 h-11 px-6 font-semibold"
                data-testid="portfolio-hero-book"
              >
                Book a Job
              </Button>
            </Link>
            <a
              href="tel:+17587230202"
              className="inline-flex items-center gap-2 px-6 h-11 rounded-md border border-white/30 text-white hover:bg-white/10 font-semibold"
              data-testid="portfolio-hero-call"
            >
              <Phone className="w-4 h-4" />
              +1 (758) 723-0202
            </a>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-slate-200 bg-white sticky top-16 md:top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 overflow-x-auto">
          <div className="flex items-center gap-2 min-w-max">
            {availableCategories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                data-testid={`portfolio-filter-${cat}`}
                className={`text-sm font-semibold px-4 py-2 rounded-full transition-colors whitespace-nowrap ${
                  activeCategory === cat
                    ? "bg-orange-500 text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {CATEGORY_LABELS[cat] || cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading ? (
          <div className="py-24 text-center" data-testid="portfolio-loading">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500" />
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="py-24 text-center text-slate-500"
            data-testid="portfolio-empty"
          >
            <p className="text-base mb-1 font-semibold text-slate-700">
              No projects yet in this category.
            </p>
            <p className="text-sm">Check back soon — new work is added regularly.</p>
          </div>
        ) : (
          <div
            className="columns-1 sm:columns-2 lg:columns-3 gap-4"
            data-testid="portfolio-grid"
          >
            {filtered.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onOpen={setSelected}
              />
            ))}
          </div>
        )}
      </section>

      <ProjectLightbox project={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
